/* eslint-disable @nx/enforce-module-boundaries */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { MaterialReactTable, MRT_Cell, MRT_TableOptions, useMaterialReactTable } from 'material-react-table';
import { Box, Typography, IconButton, Tooltip, useTheme, Chip, Skeleton } from '@mui/material';
import infoIcon from 'packages/novous_web/src/assets/icons/infoIcon.svg';
import NoDataIcon from 'packages/novous_web/src/assets/icons/NoData.svg';
import ArrowLeftIcon from 'packages/novous_web/src/assets/icons/ArrowLeft.svg';
import ArrowRightIcon from 'packages/novous_web/src/assets/icons/next.svg';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import menuIcon from 'packages/novous_web/src/assets/icons/new-menu.svg';

interface NumericalAnalysisData {
  fleetId: string;
  date: string;
  hourlyBucket: string;
  safetyLabel: 'High' | 'Medium' | 'Low';
  highSpeedPhone: number;
  highSpeedSmoke: number;
  highSpeedSeatbelt: number;
  highSpeedLane: number;
  totalViolations: number;
  flag: boolean;
}

interface NumericalAnalysisTableCompilanceProps {
  startDate?: string;
  endDate?: string;
  fleetId?: string;
  paginatedData?: any;
  onPageChange?: (cursor: any) => void;
  infoTooltip?: string;
}

const NumericalAnalysisTableCompilance: React.FC<NumericalAnalysisTableCompilanceProps> = ({
  startDate,
  endDate,
  fleetId,
  paginatedData,
  onPageChange,
  infoTooltip,
}) => {
  const theme = useTheme();

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pagesData, setPagesData] = useState<NumericalAnalysisData[][]>([]);
  const [nextCursor, setNextCursor] = useState<any>(null);
  const [hasMorePages, setHasMorePages] = useState(false);
  const { user } = useAuth();

  // Mock data for now - replace with actual API call
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Reset pagination when filters change (but not when paginatedData changes)
    if (startDate || endDate || fleetId) {
      setCurrentPage(1);
      setPagesData([]);
      setNextCursor(null);
      setHasMorePages(false);
      setLoading(true);
    }
  }, [startDate, endDate, fleetId]);

  useEffect(() => {
    // Process paginated data when it changes
    if (paginatedData && paginatedData.data) {
      console.log('=== NumericalAnalysisTableCompilance - Processing Paginated Data ===');
      console.log('Chart ID: 1039');
      console.log('Current Page:', currentPage);
      console.log('Raw Paginated Data:', paginatedData);
      console.log('Data Length:', paginatedData.data.length);
      console.log('Next Cursor:', paginatedData.next_cursor);

      const transformedData: NumericalAnalysisData[] = paginatedData.data.map((item: any) => {
        // Extract high-speed violations from response
        const highSpeedPhone = item.high_speed_phone || 0;
        const highSpeedSmoke = item.high_speed_smoke || 0;
        const highSpeedSeatbelt = item.high_speed_seatbelt || 0;
        const highSpeedLane = item.high_speed_lane || 0;

        // Calculate total violations
        const totalViolations = highSpeedPhone + highSpeedSmoke + highSpeedSeatbelt + highSpeedLane;

        // Determine safety label based on total violations
        let safetyLabel: 'High' | 'Medium' | 'Low' = 'Low';

        if (totalViolations >= 15) {
          safetyLabel = 'High'; // High risk - Red (15+ violations)
        } else if (totalViolations >= 7) {
          safetyLabel = 'Medium'; // Medium risk - Orange (7-14 violations)
        } else {
          safetyLabel = 'Low'; // Low risk - Green (< 7 violations)
        }

        return {
          fleetId: item.vehicle_id || 'N/A',
          date: item.record_date || 'N/A',
          hourlyBucket: `${item.record_hour || 0}-${(item.record_hour || 0) + 1}`,
          safetyLabel: safetyLabel,
          highSpeedPhone: highSpeedPhone,
          highSpeedSmoke: highSpeedSmoke,
          highSpeedSeatbelt: highSpeedSeatbelt,
          highSpeedLane: highSpeedLane,
          totalViolations: totalViolations,
          flag: totalViolations >= 15, // Flag for High risk
        };
      });

      console.log('Transformed Data:', transformedData);

      // Store the current page data
      setPagesData((prevPages) => {
        const updatedPages = [...prevPages];
        updatedPages[currentPage - 1] = transformedData;
        console.log('Updated pages data:', updatedPages);
        return updatedPages;
      });

      // Store next_cursor if it exists
      if (paginatedData.next_cursor) {
        setNextCursor(paginatedData.next_cursor);
        setHasMorePages(true);
        console.log('Has more pages: true');
      } else {
        setNextCursor(null);
        setHasMorePages(false);
        console.log('Has more pages: false');
      }

      setLoading(false);
      console.log('=== End Processing ===');
    }
  }, [paginatedData]);

  const cumulativeCount = useMemo(() => {
    return pagesData.slice(0, currentPage).reduce((sum, page) => sum + (page?.length || 0), 0);
  }, [pagesData, currentPage]);

  const currentPageData: NumericalAnalysisData[] = pagesData[currentPage - 1] ?? [];

  // Create skeleton data for loading state (4 rows)
  const skeletonData: NumericalAnalysisData[] = Array(4)
    .fill(null)
    .map((_, index) => ({
      fleetId: `skeleton-${index}`,
      date: '2025-01-01',
      hourlyBucket: '0-1',
      safetyLabel: 'Low' as 'High' | 'Medium' | 'Low',
      highSpeedPhone: 0,
      highSpeedSmoke: 0,
      highSpeedSeatbelt: 0,
      highSpeedLane: 0,
      totalViolations: 0,
      flag: false,
    }));

  // Use skeleton data if loading or no data available, otherwise use actual data
  const displayData = loading || currentPageData.length === 0 ? skeletonData : currentPageData;

  const getSafetyLabelColor = (label: string) => {
    switch (label) {
      case 'High':
        return { background: '#F7E1E1', color: '#C83333' }; // Red
      case 'Medium':
        return { background: '#FFF4E5', color: '#FF9800' }; // Orange
      case 'Low':
        return { background: '#D9EDE6', color: '#219653' }; // Green
      default:
        return { background: '#F5F5F5', color: '#9E9E9E' }; // Grey
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'date',
        header: 'Date',
        Cell: ({ cell }: { cell: MRT_Cell<NumericalAnalysisData> }) => {
          const isLoading = loading || currentPageData.length === 0;
          if (isLoading) {
            return (
              <Skeleton
                variant="text"
                width={120}
                height={20}
                animation="wave"
                sx={{
                  bgcolor: 'rgba(85, 71, 221, 0.08)',
                  '&::after': {
                    animationDuration: '1s',
                  },
                }}
              />
            );
          }
          const dateValue = cell.getValue<string>();
          const formatDate = (dateString: string) => {
            if (!dateString) return 'N/A';
            try {
              const date = new Date(dateString);
              const day = date.getDate();
              const monthNames = [
                'Jan',
                'Feb',
                'Mar',
                'Apr',
                'May',
                'Jun',
                'Jul',
                'Aug',
                'Sep',
                'Oct',
                'Nov',
                'Dec',
              ];
              const month = monthNames[date.getMonth()];
              const year = date.getFullYear();
              return `${day} ${month} ${year}`;
            } catch {
              return dateString;
            }
          };
          return (
            <Typography variant="body2" sx={{ fontWeight: 400 }}>
              {formatDate(dateValue)}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'hourlyBucket',
        header: 'Hourly Bucket',
        Cell: ({ cell }: { cell: MRT_Cell<NumericalAnalysisData> }) => {
          const isLoading = loading || currentPageData.length === 0;
          if (isLoading) {
            return (
              <Skeleton
                variant="text"
                width={80}
                height={20}
                animation="wave"
                sx={{
                  bgcolor: 'rgba(85, 71, 221, 0.08)',
                  '&::after': {
                    animationDuration: '1s',
                  },
                }}
              />
            );
          }
          return (
            <Typography variant="body2" sx={{ fontWeight: 400 }}>
              {cell.getValue<string>()}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'highSpeedPhone',
        header: 'High Speed Phone',
        Cell: ({ cell }: { cell: MRT_Cell<NumericalAnalysisData> }) => {
          const isLoading = loading || currentPageData.length === 0;
          if (isLoading) {
            return (
              <Skeleton
                variant="text"
                width={60}
                height={20}
                animation="wave"
                sx={{
                  bgcolor: 'rgba(85, 71, 221, 0.08)',
                  '&::after': {
                    animationDuration: '1s',
                  },
                }}
              />
            );
          }
          return (
            <Typography variant="body2" sx={{ fontWeight: 400 }}>
              {cell.getValue<number>()}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'highSpeedSmoke',
        header: 'High Speed Smoke',
        Cell: ({ cell }: { cell: MRT_Cell<NumericalAnalysisData> }) => {
          const isLoading = loading || currentPageData.length === 0;
          if (isLoading) {
            return (
              <Skeleton
                variant="text"
                width={60}
                height={20}
                animation="wave"
                sx={{
                  bgcolor: 'rgba(85, 71, 221, 0.08)',
                  '&::after': {
                    animationDuration: '1s',
                  },
                }}
              />
            );
          }
          return (
            <Typography variant="body2" sx={{ fontWeight: 400 }}>
              {cell.getValue<number>()}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'highSpeedSeatbelt',
        header: 'High Speed Seatbelt',
        Cell: ({ cell }: { cell: MRT_Cell<NumericalAnalysisData> }) => {
          const isLoading = loading || currentPageData.length === 0;
          if (isLoading) {
            return (
              <Skeleton
                variant="text"
                width={60}
                height={20}
                animation="wave"
                sx={{
                  bgcolor: 'rgba(85, 71, 221, 0.08)',
                  '&::after': {
                    animationDuration: '1s',
                  },
                }}
              />
            );
          }
          return (
            <Typography variant="body2" sx={{ fontWeight: 400 }}>
              {cell.getValue<number>()}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'highSpeedLane',
        header: 'High Speed Lane',
        Cell: ({ cell }: { cell: MRT_Cell<NumericalAnalysisData> }) => {
          const isLoading = loading || currentPageData.length === 0;
          if (isLoading) {
            return (
              <Skeleton
                variant="text"
                width={60}
                height={20}
                animation="wave"
                sx={{
                  bgcolor: 'rgba(85, 71, 221, 0.08)',
                  '&::after': {
                    animationDuration: '1s',
                  },
                }}
              />
            );
          }
          return (
            <Typography variant="body2" sx={{ fontWeight: 400 }}>
              {cell.getValue<number>()}
            </Typography>
          );
        },
      },
    ],
    [loading, currentPageData],
  );

  const tableOptions: MRT_TableOptions<NumericalAnalysisData> = {
    columns: columns,
    data: displayData,
    enableColumnActions: false,
    enablePagination: false,
    enableColumnFilters: false,
    enableSorting: false,
    enableGlobalFilter: false,
    enableDensityToggle: false,
    enableHiding: false,
    enableBottomToolbar: false,
    enableTopToolbar: false,
    enableFullScreenToggle: false,
    enableColumnResizing: false,
    enableToolbarInternalActions: false,
    layoutMode: 'semantic',
    muiTableProps: {
      sx: {
        tableLayout: 'auto',
        width: '100%',
        '& .MuiTableRow-root': {
          height: '48px !important',
          minHeight: '48px !important',
          maxHeight: '48px !important',
        },
        '& .MuiTableCell-root': {
          height: '48px !important',
          minHeight: '48px !important',
          maxHeight: '48px !important',
          padding: '0px 12px !important',
          verticalAlign: 'middle !important',
          boxSizing: 'border-box !important',
          // lineHeight: '1 !important',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        '& .MuiTableHead-root .MuiTableRow-root': {
          height: '48px !important',
          minHeight: '48px !important',
          maxHeight: '48px !important',
          boxShadow: 'none !important',
        },
        '& .MuiTableHead-root .MuiTableCell-root': {
          height: '48px !important',
          minHeight: '48px !important',
          maxHeight: '48px !important',
        },
        '& .MuiTableBody-root .MuiTableRow-root': {
          height: '48px !important',
          minHeight: '48px !important',
          maxHeight: '48px !important',
        },
        '& .MuiTableBody-root .MuiTableCell-root': {
          height: '48px !important',
          minHeight: '48px !important',
          maxHeight: '48px !important',
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        width: '100%',
        maxWidth: '100%',
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: '3px',
        },
      },
    },
    muiTableBodyProps: {
      sx: {
        padding: 0,
        margin: 0,
      },
    },
    muiTableHeadProps: {
      sx: {
        backgroundColor: '#F6F6FD',
        border: '1px solid #F0EFF8',
        borderRadius: '8px',
        height: '48px',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: 'transparent',
        border: 'none',
        borderRadius: '0px',
        fontWeight: 600,
        fontStyle: 'normal',
        fontSize: '14px',
        // lineHeight: '21px',
        letterSpacing: '0%',
        color: '#1B1B1B',
        height: '48px !important',
        minHeight: '48px !important',
        maxHeight: '48px !important',
        padding: '0px 12px !important',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: 'table-cell',
        '&:first-of-type': {
          borderRadius: '8px 0 0 8px',
        },
        '&:last-of-type': {
          borderRadius: '0 8px 8px 0',
        },
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        height: '48px !important',
        minHeight: '48px !important',
        maxHeight: '48px !important',
        '& .MuiTableCell-root': {
          height: '48px !important',
          minHeight: '48px !important',
          maxHeight: '48px !important',
          verticalAlign: 'middle',
          padding: '0px 12px !important',
        },
        backgroundColor:
          row.index % 2 === 0 ? (theme.palette.mode === 'dark' ? '#121212' : '#F0EFF8') : 'inherit',
      },
    }),
    muiTableBodyCellProps: {
      sx: {
        height: '48px !important',
        minHeight: '48px !important',
        maxHeight: '48px !important',
        padding: '0px 12px !important',
        verticalAlign: 'middle',
        // lineHeight: '1 !important',
        boxSizing: 'border-box !important',
      },
    },
  };

  const tableInstance = useMaterialReactTable(tableOptions);

  // Calculate total pages based on available data and hasMorePages flag
  const totalPages = hasMorePages ? currentPage + 1 : currentPage;

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    console.log('=== NumericalAnalysisTableCompilance - Page Change ===');
    console.log('Chart ID: 1039');
    console.log('Current Page:', currentPage);
    console.log('New Page Requested:', page);
    console.log('Has Data For Page:', !!pagesData[page - 1]);
    console.log('Next Cursor Available:', !!nextCursor);
    console.log('Has More Pages:', hasMorePages);

    // Check if we already have data for this page
    if (pagesData[page - 1]) {
      // Data already exists, just change page
      console.log('Data exists for page', page, 'switching to it');
      setCurrentPage(page);
    } else if (page > currentPage && onPageChange && nextCursor) {
      // Need to fetch new page data
      console.log('Fetching new page data with cursor:', nextCursor);
      setLoading(true);
      setCurrentPage(page);
      onPageChange(nextCursor);
    } else {
      // Fallback
      console.log('Fallback: Switching page without data fetch');
      setCurrentPage(page);
    }
    console.log('=== End Page Change ===');
  };

  if (error) {
    return (
      <Box className="w-full h-full flex flex-col border rounded-md p-4 gap-2 items-start mt-2 bg-white">
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box className="w-full h-full flex flex-col border rounded-md p-4 gap-2 items-start bg-white">
      {/* Header */}
      <Box className="w-full" display="flex" justifyContent="space-between" alignItems="center">
        <Box className="w-full" display="flex" alignItems="center" gap={1}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontStyle: 'normal',
              fontSize: '18px',
              lineHeight: '27px',
              letterSpacing: '0%',
              color: '#1B1B1B',
            }}
          >
            Numerical Analysis of Recent Week
          </Typography>
          <Tooltip title={infoTooltip} arrow>
            <img src={infoIcon} alt="info icon" width={16} height={16} />
          </Tooltip>
        </Box>
        {/* <Box display="flex" alignItems="center" gap={1}>
          <IconButton size="medium" sx={{ p: 0 }}>
            <img src={menuIcon} alt="Table" />
          </IconButton>
        </Box> */}
      </Box>

      {/* Scrollable table container or No Data placeholder */}
      {!loading && currentPageData.length === 0 ? (
        <Box
          sx={{
            width: '100%',
            minHeight: '300px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img src={NoDataIcon} alt="No data" className="w-48 h-48 opacity-50" />
        </Box>
      ) : (
        <div className="table-wrapper" style={{ overflow: 'auto', width: '100%' }}>
          <MaterialReactTable table={tableInstance} />
        </div>
      )}

      {/* Pagination - Only show if there's data */}
      {!loading && currentPageData.length > 0 && (
        <Box display="flex" justifyContent="center" alignItems="center" width={'100%'} gap={1} mt={2}>
          <IconButton
            size="small"
            onClick={() => handlePageChange(null as any, currentPage - 1)}
            disabled={currentPage === 1}
            sx={{
              border: '1px solid',
              borderColor: theme.palette.primary.main,
              borderRadius: '8px',
              width: '32px',
              height: '32px',
              '&:disabled': {
                opacity: 0.5,
                borderColor: '#ccc',
              },
            }}
          >
            <img src={ArrowLeftIcon} alt="Previous" style={{ width: '16px', height: '16px' }} />
          </IconButton>

          {/* Page numbers - sliding window of 3 */}
          {(() => {
            const visiblePages: number[] = [];
            const windowSize = 3;

            // Calculate the start of the window
            let startPage = Math.max(1, currentPage - 1);

            // Adjust if we're near the end
            if (currentPage === totalPages && totalPages > 1) {
              startPage = Math.max(1, totalPages - windowSize + 1);
            }

            // Generate visible page numbers
            for (let i = 0; i < windowSize && startPage + i <= totalPages; i++) {
              visiblePages.push(startPage + i);
            }

            return visiblePages.map((pageNum) => (
              <Box
                key={pageNum}
                onClick={() => handlePageChange(null as any, pageNum)}
                sx={{
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: pageNum === currentPage ? theme.palette.primary.main : 'transparent',
                  color: pageNum === currentPage ? '#FFFFFF' : '#808080',
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: '18px',
                  textAlign: 'center',
                  '&:hover': {
                    backgroundColor: pageNum === currentPage ? theme.palette.primary.main : '#F5F5F5',
                  },
                }}
              >
                {pageNum}
              </Box>
            ));
          })()}

          <IconButton
            size="small"
            onClick={() => handlePageChange(null as any, currentPage + 1)}
            disabled={!hasMorePages}
            sx={{
              border: '1px solid',
              borderColor: theme.palette.primary.main,
              borderRadius: '8px',
              width: '32px',
              height: '32px',
              '&:disabled': {
                opacity: 0.5,
                borderColor: '#ccc',
              },
            }}
          >
            <img src={ArrowRightIcon} alt="Next" style={{ width: '16px', height: '16px' }} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default NumericalAnalysisTableCompilance;
